import { useCallback } from 'react';
import dagre from 'dagre';
import { Node, Edge } from 'reactflow';

import {
  MISSION_NODE_WIDTH,
  MISSION_NODE_MIN_HEIGHT,
  QUEST_NODE_WIDTH,
  QUEST_NODE_HEIGHT,
  QUESTS_PER_MISSION_ROW,
  MISSION_TO_QUEST_VERTICAL_GAP,
  MISSION_QUEST_GAP_X,
  MISSION_QUEST_GAP_Y,
  START_NODE_WIDTH,
  START_NODE_HEIGHT,
  NODE_HORIZONTAL_SPACING
} from '../constants';

function getMissionQuestFootprintWidth(node: Node): number {
  if (node.type !== 'missionNode') return MISSION_NODE_WIDTH;

  const questCount = node.data?.mission?.quests?.length ?? 0;
  if (questCount === 0) return MISSION_NODE_WIDTH;

  const rowWidth =
    Math.min(questCount, QUESTS_PER_MISSION_ROW) * QUEST_NODE_WIDTH +
    Math.max(0, Math.min(questCount, QUESTS_PER_MISSION_ROW) - 1) * MISSION_QUEST_GAP_X;

  return Math.max(MISSION_NODE_WIDTH, rowWidth);
}

function getNodeDimensions(node: Node): { width: number; height: number } {
  if (node.type === 'startNode') {
    return { width: START_NODE_WIDTH, height: START_NODE_HEIGHT };
  }

  if (node.type === 'questNode') {
    return { width: QUEST_NODE_WIDTH, height: QUEST_NODE_HEIGHT };
  }

  return {
    width: getMissionQuestFootprintWidth(node),
    height: MISSION_NODE_MIN_HEIGHT
  };
}

export default function useFlowLayout() {
  const getLayoutedElements = useCallback(
    (nodes: Node[], edges: Edge[], direction = 'LR') => {
      const layoutBaseNodes = nodes.filter((n) => n.type !== 'questNode');
      const questNodes = nodes.filter((n) => n.type === 'questNode');
      const layoutBaseNodeIds = new Set(layoutBaseNodes.map((n) => n.id));

      // Only pass mission sequence edges to dagre
      const topLevelEdges = edges.filter((edge) => {
        if (edge.type === 'questDependencyEdge') return false;
        if (edge.type === 'missionQuestEdge') return false;
        return layoutBaseNodeIds.has(edge.source) && layoutBaseNodeIds.has(edge.target);
      });

      const dagreGraph = new dagre.graphlib.Graph();
      dagreGraph.setDefaultEdgeLabel(() => ({}));
      dagreGraph.setGraph({
        rankdir: direction,
        nodesep: NODE_HORIZONTAL_SPACING,
        ranksep: NODE_HORIZONTAL_SPACING,
        marginx: 60,
        marginy: 60
      });

      layoutBaseNodes.forEach((node) => {
        const { width, height } = getNodeDimensions(node);
        dagreGraph.setNode(node.id, { width, height });
      });

      topLevelEdges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
      });

      dagre.layout(dagreGraph);

      const layoutedTopLevel = layoutBaseNodes.map((node) => {
        const dagreNode = dagreGraph.node(node.id);
        const isMissionNode = node.type === 'missionNode';
        const renderWidth = isMissionNode ? MISSION_NODE_WIDTH : START_NODE_WIDTH;
        const renderHeight = isMissionNode ? MISSION_NODE_MIN_HEIGHT : START_NODE_HEIGHT;

        return {
          ...node,
          position: {
            x: dagreNode.x - renderWidth / 2,
            y: dagreNode.y - renderHeight / 2
          }
        };
      });

      // Place quest nodes around each mission after mission layout is done.
      const missionPositionMap = new Map<string, Node>();
      layoutedTopLevel.forEach((node) => {
        if (node.type === 'missionNode') {
          missionPositionMap.set(node.id, node);
        }
      });

      const questNodesByMission = new Map<number, Node[]>();
      questNodes.forEach((node) => {
        const missionId = Number(node.data?.missionId);
        if (!Number.isFinite(missionId)) return;
        const missionQuests = questNodesByMission.get(missionId) ?? [];
        missionQuests.push(node);
        questNodesByMission.set(missionId, missionQuests);
      });

      const layoutedQuestNodes: Node[] = [];
      questNodesByMission.forEach((missionQuests, missionId) => {
        const missionNode = missionPositionMap.get(`mission-${missionId}`);
        if (!missionNode) {
          layoutedQuestNodes.push(...missionQuests);
          return;
        }

        const missionCenterX = missionNode.position.x + MISSION_NODE_WIDTH / 2;
        const missionBottomY = missionNode.position.y + MISSION_NODE_MIN_HEIGHT;
        const rowCount = Math.ceil(missionQuests.length / QUESTS_PER_MISSION_ROW);

        for (let row = 0; row < rowCount; row += 1) {
          const rowStartIndex = row * QUESTS_PER_MISSION_ROW;
          const rowNodes = missionQuests.slice(rowStartIndex, rowStartIndex + QUESTS_PER_MISSION_ROW);
          const rowWidth =
            rowNodes.length * QUEST_NODE_WIDTH + Math.max(0, rowNodes.length - 1) * MISSION_QUEST_GAP_X;
          const startX = missionCenterX - rowWidth / 2;
          const y = missionBottomY + MISSION_TO_QUEST_VERTICAL_GAP + row * (QUEST_NODE_HEIGHT + MISSION_QUEST_GAP_Y);

          rowNodes.forEach((node, column) => {
            layoutedQuestNodes.push({
              ...node,
              position: {
                x: startX + column * (QUEST_NODE_WIDTH + MISSION_QUEST_GAP_X),
                y
              }
            });
          });
        }
      });

      return { nodes: [...layoutedTopLevel, ...layoutedQuestNodes], edges };
    },
    []
  );

  return { getLayoutedElements };
}
